/**
 * Info Banner Drawer Component
 * Componente reutilizável para exibir informações em menu (desktop) ou drawer (mobile)
 * Usado por ImpersonationBanner e TrialPeriodBanner
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Backdrop
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ReactElement } from 'react';

interface InfoBannerDrawerProps {
  isMobile?: boolean;
  anchorEl: HTMLElement | null;
  drawerOpen: boolean;
  onClose: () => void;
  headerIcon: ReactElement;
  headerTitle: string;
  headerSubtitle: string;
  headerBackground?: string; // Gradient ou cor de fundo
  headerColor?: string; // Cor do texto no header (default: #21316a para fundo claro)
  children: React.ReactNode;
}

const InfoBannerDrawer: React.FC<InfoBannerDrawerProps> = ({
  isMobile: isMobileProp,
  anchorEl,
  drawerOpen,
  onClose,
  headerIcon,
  headerTitle,
  headerSubtitle,
  headerBackground = 'linear-gradient(90deg, #f7f8fc 0%, #d1d8e4 100%)',
  headerColor = '#21316a',
  children
}) => {
  const theme = useTheme();
  const isMobileFromTheme = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = isMobileProp ?? isMobileFromTheme;

  const menuContent = (
    <Box sx={{ width: '100%', maxWidth: isMobile ? '100%' : 360 }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background: headerBackground,
          color: headerColor,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Box sx={{ fontSize: 24, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {headerIcon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              fontSize: '1rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {headerTitle}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              opacity: 0.9, 
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {headerSubtitle}
          </Typography>
        </Box>
        {isMobile && (
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: headerColor, flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', color: 'text.primary' }}>
        {children}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Menu para Desktop */}
      {!isMobile && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: 4,
              overflow: 'hidden',
              width: 360,
              maxWidth: '90vw',
              bgcolor: 'background.paper'
            }
          }}
        >
          {menuContent}
        </Menu>
      )}

      {/* Drawer para Mobile */}
      {isMobile && drawerOpen && (
        <>
          <Backdrop
            open={drawerOpen}
            onClick={onClose}
            sx={{
              zIndex: (theme) => theme.zIndex.appBar + 1,
              top: '56px', // Começar logo abaixo do AppBar
              height: 'calc(100vh - 56px)',
              position: 'fixed'
            }}
          />
          <Box
            sx={{
              position: 'fixed',
              top: '56px', // Posicionar logo abaixo da barra superior (minHeight do AppBar mobile)
              left: 0,
              right: 0,
              width: '100%',
              maxHeight: 'calc(85vh - 56px)',
              overflow: 'auto',
              zIndex: (theme) => theme.zIndex.appBar + 2,
              bgcolor: 'background.paper',
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              boxShadow: 4
            }}
          >
            {menuContent}
          </Box>
        </>
      )}
    </>
  );
};

export default InfoBannerDrawer;

